import { useTranslation } from 'react-i18next';
import { cn } from '@c2fo/liquidity';
import { BuyerCurrencyGroupWithStats } from '@/enterprise/components/InvoiceCentral/useGroupByBuyerCurrency';
import useLockedInvoiceStats from '@/enterprise/data/useLockedInvoiceStats';
import usePlayableInvoiceStats from '@/enterprise/data/usePlayableInvoiceStats';
import IntercomDataTarget from '@/reporting/IntercomDataTarget';
import handleNaN from '@/utils/handleNaN';
import useLocaleFormat from '@/utils/useLocaleFormat';
import EmptyRender from '../EmptyRender';

const InvoiceCentralStats = ({
  buyer,
  makerOrganizationUuid,
}: {
  buyer: BuyerCurrencyGroupWithStats;
  makerOrganizationUuid: string;
}) => {
  const { asCurrency, asNumber, asDateStringText } = useLocaleFormat();
  const { takerMarkets, currency, stats } = buyer;
  const { data: playableInvoiceStats } = usePlayableInvoiceStats({
    makerOrganizationUuid,
    takerMarkets,
  });
  const { data: ineligibleInvoiceStats } = useLockedInvoiceStats({
    makerOrganizationUuid,
    takerMarkets,
  });

  const { t } = useTranslation();

  const totalAmount = (playableInvoiceStats?.totalAmount || 0) + (ineligibleInvoiceStats?.totalAmount || 0);
  const totalCount = (playableInvoiceStats?.totalCount || 0) + (ineligibleInvoiceStats?.totalCount || 0);

  return (
    <IntercomDataTarget className="rounded-2xl bg-canvas" target="invoiceCentralSummary">
      <div className="grid grid-cols-1 border-none p-6 sm:grid-cols-2 lg:grid-cols-4 lg:[&>*:nth-child(2)]:border-r lg:[&>*:nth-child(2)]:border-deepGray-500/[12%] sm:[&>*:nth-child(odd)]:border-r sm:[&>*:nth-child(odd)]:border-deepGray-500/[12%]">
        <div className="mb-3 text-center sm:mb-0 sm:content-center">
          <div className="text-sm text-text-secondary">{t('core.totalDivisions')}</div>
          <div className={cn('text-xl font-medium')}>{asNumber({ value: takerMarkets.length })}</div>
        </div>
        <div className="relative mb-3 flex items-center justify-center lg:mb-0">
          <div className="text-center">
            <div className="text-sm text-text-secondary">{t('core.totalAmount')}</div>
            <div className={cn('text-xl font-medium')}>{asCurrency(totalAmount, currency)}</div>
            <div className={cn('text-sm text-text-secondary')}>
              {t('core.invoiceWithCount', {
                count: totalCount,
                formattedCount: asNumber({ value: totalCount }),
              })}
            </div>
          </div>
        </div>
        <div className="mt-3 text-center lg:mt-0">
          <div className="text-sm text-text-secondary">{t('invoiceCentral.totalEligibleAmount')}</div>
          <div className={cn('text-xl font-medium')}>{asCurrency(playableInvoiceStats.totalAmount, currency)}</div>
          <div className={cn('text-sm text-text-secondary')}>
            {t('core.invoiceWithCount', {
              count: playableInvoiceStats.totalCount,
              formattedCount: asNumber({ value: playableInvoiceStats.totalCount }),
            })}
          </div>
        </div>
        <div className="mt-6 text-center sm:mt-3 lg:mt-0">
          <div className="text-sm text-text-secondary">{t('core.getPaidAsEarlyAs')}</div>
          <div className={cn('text-xl font-medium')}>
            <EmptyRender condition={!!stats.nextGoodFundsDateWithEligibleInvoices}>
              {stats.nextGoodFundsDateWithEligibleInvoices &&
                asDateStringText({ date: stats.nextGoodFundsDateWithEligibleInvoices })}
            </EmptyRender>
          </div>
          <div className={cn('flex items-center justify-center space-x-2 text-sm text-text-secondary')}>
            {t('core.averageDaysPaidEarly')}: {asNumber({ value: handleNaN(stats.avgDaysPaidEarly) })}
          </div>
        </div>
      </div>
    </IntercomDataTarget>
  );
};

export default InvoiceCentralStats;
